import { storeToRefs } from 'pinia'
import { useApiMonOrga } from '~/composables/api/useApiMonOrga'
import { useAvatarSettingsStore } from '~~/stores/avatarSettings'

export type EAgentAvatarDisplayMode = 'teams' | 'initials' | 'avatar' | null

type AgentAvatarConfig = Record<string, unknown> | null

export type MyAgentAvatarSettings = {
  isConfigured: boolean
  avatarDisplayMode: EAgentAvatarDisplayMode
  avatarConfig: AgentAvatarConfig
}

export const AGENT_AVATAR_STORAGE_KEY = 'monorga.me.agentAvatar'
export const AGENT_AVATAR_COOKIE_KEY = 'monorga_avatar_cache'

const AGENT_AVATAR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30

function isMyAgentAvatarSettings(value: unknown): value is MyAgentAvatarSettings {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const settings = value as Partial<MyAgentAvatarSettings>
  return (
    typeof settings.isConfigured === 'boolean'
    && (
      settings.avatarDisplayMode === 'teams'
      || settings.avatarDisplayMode === 'initials'
      || settings.avatarDisplayMode === 'avatar'
      || settings.avatarDisplayMode === null
    )
    && (settings.avatarConfig === null || typeof settings.avatarConfig === 'object')
  )
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function readAvatarSettingsFromStorage(): MyAgentAvatarSettings | null {
  if (!import.meta.client) {
    return null
  }
  try {
    const raw = localStorage.getItem(AGENT_AVATAR_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = safeParseJson(raw)
    if (!isMyAgentAvatarSettings(parsed)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function readAvatarSettingsFromCookie(cookieValue: unknown): MyAgentAvatarSettings | null {
  if (cookieValue === null || cookieValue === undefined) {
    return null
  }
  if (typeof cookieValue === 'string') {
    if (cookieValue.trim() === '') {
      return null
    }
    const parsed = safeParseJson(cookieValue)
    if (!isMyAgentAvatarSettings(parsed)) {
      return null
    }
    return parsed
  }
  if (typeof cookieValue === 'object' && isMyAgentAvatarSettings(cookieValue)) {
    return cookieValue
  }
  return null
}

function preferCachedAvatarSettings(
  cookieSettings: MyAgentAvatarSettings | null,
  storageSettings: MyAgentAvatarSettings | null,
): MyAgentAvatarSettings | null {
  if (cookieSettings && storageSettings) {
    if (cookieSettings.isConfigured && !storageSettings.isConfigured) {
      return cookieSettings
    }
    if (!cookieSettings.isConfigured && storageSettings.isConfigured) {
      return storageSettings
    }
    return cookieSettings
  }
  return cookieSettings ?? storageSettings
}

function writeAvatarSettingsToStorage(settings: MyAgentAvatarSettings): void {
  if (!import.meta.client) {
    return
  }
  try {
    localStorage.setItem(AGENT_AVATAR_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    /* quota / mode privé */
  }
}

function writeAvatarSettingsToCookie(
  settings: MyAgentAvatarSettings,
  writeCookie: (value: MyAgentAvatarSettings) => void,
): void {
  if (!import.meta.client) {
    return
  }
  try {
    writeCookie(settings)
  } catch {
    /* cookie trop volumineux */
  }
}

function persistAvatarSettings(
  settings: MyAgentAvatarSettings,
  writeCookie: (value: MyAgentAvatarSettings) => void,
): void {
  writeAvatarSettingsToStorage(settings)
  writeAvatarSettingsToCookie(settings, writeCookie)
}

export function useMyAgentAvatarSettings() {
  const avatarSettingsStore = useAvatarSettingsStore()
  const { settings, isReady } = storeToRefs(avatarSettingsStore)
  const avatarCookie = useCookie<MyAgentAvatarSettings | null>(AGENT_AVATAR_COOKIE_KEY, {
    maxAge: AGENT_AVATAR_COOKIE_MAX_AGE_SECONDS,
    sameSite: 'lax',
    secure: true,
    path: '/',
    default: () => null,
  })
  let avatarWriteEpoch = 0

  if (import.meta.client && !avatarSettingsStore.isReady) {
    const cachedSettings = preferCachedAvatarSettings(
      readAvatarSettingsFromCookie(avatarCookie.value),
      readAvatarSettingsFromStorage(),
    )
    if (cachedSettings !== null) {
      avatarSettingsStore.setSettings(cachedSettings)
    }
  }

  async function refresh(): Promise<void> {
    if (import.meta.server) {
      return
    }
    const seenEpoch = avatarWriteEpoch
    try {
      const response = await useApiMonOrga<MyAgentAvatarSettings>('GET', '/v1/me/agent-avatar')
      if (seenEpoch !== avatarWriteEpoch) {
        return
      }
      avatarSettingsStore.setSettings(response)
      persistAvatarSettings(response, (value) => {
        avatarCookie.value = value
      })
      avatarSettingsStore.setReady(true)
    } catch (error) {
      if (seenEpoch !== avatarWriteEpoch) {
        return
      }
      const cachedSettings = preferCachedAvatarSettings(
        readAvatarSettingsFromCookie(avatarCookie.value),
        readAvatarSettingsFromStorage(),
      )
      if (cachedSettings !== null) {
        avatarSettingsStore.setSettings(cachedSettings)
        console.warn(
          '[useMyAgentAvatarSettings] Impossible de rafraîchir depuis le serveur, utilisation du cache local.',
          error,
        )
      } else {
        avatarSettingsStore.setSettings({
          isConfigured: false,
          avatarDisplayMode: null,
          avatarConfig: null,
        })
      }
      avatarSettingsStore.setReady(true)
    }
  }

  async function update(payload: {
    avatarDisplayMode: Exclude<EAgentAvatarDisplayMode, null>
    avatarConfig?: Record<string, unknown> | null
  }): Promise<void> {
    if (import.meta.server) {
      return
    }
    avatarWriteEpoch += 1
    try {
      const response = await useApiMonOrga<MyAgentAvatarSettings>('PUT', '/v1/me/agent-avatar', undefined, payload)
      avatarSettingsStore.setSettings(response)
      avatarWriteEpoch += 1
      persistAvatarSettings(response, (value) => {
        avatarCookie.value = value
      })
      avatarSettingsStore.setReady(true)
    } catch (error) {
      avatarSettingsStore.setReady(true)
      throw error
    }
  }

  return {
    settings,
    isReady,
    refresh,
    update,
  }
}
